import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, doc, limit, onSnapshot, orderBy, query, setDoc, where, type Query } from 'firebase/firestore';
import { getFirestoreDB } from '../lib/firebase';
import type { Page } from '../types';

/** One "needs attention" item shown in the POS notification bell. */
export interface StaffNotification {
  id: string;
  kind: 'catering' | 'chat' | 'feedback' | 'meal_plan_exception';
  title: string;
  detail: string;
  /** Epoch ms of the latest activity — newest-first sorting, and part of the "seen" key. */
  at: number;
  page: Page;
}

type Source = {
  kind: StaffNotification['kind'];
  build: () => Query;
  map: (id: string, data: Record<string, any>) => StaffNotification;
};

const OCCASIONS: Record<string, string> = {
  birthday: 'Birthday',
  wedding: 'Wedding / engagement',
  private_gathering: 'Private get-together',
  catch_up: 'Catch-up',
  corporate: 'Corporate',
  other: 'Event',
};

const toMillis = (v: unknown): number => {
  if (typeof v === 'number') return v;
  if (v && typeof (v as { toMillis?: () => number }).toMillis === 'function') return (v as { toMillis: () => number }).toMillis();
  return 0;
};

/**
 * "Seen" is tied to the item's latest activity: a chat thread that gets a new message after being
 * opened gets a new key, so it alerts again.
 */
const seenKey = (item: StaffNotification) => `${item.id}_${item.at}`;

const SEEN_COLLECTION = 'staff_alert_seen';
const LOCAL_SEEN_KEY = 'kingspark_staff_alert_seen';

function readLocalSeen(): Set<string> {
  try {
    const raw = window.localStorage.getItem(LOCAL_SEEN_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function writeLocalSeen(keys: Set<string>) {
  try {
    // Keep the most recent 300 so this never grows unbounded.
    window.localStorage.setItem(LOCAL_SEEN_KEY, JSON.stringify([...keys].slice(-300)));
  } catch {
    /* storage unavailable — shared (Firestore) seen state still applies */
  }
}

/**
 * Live "needs attention" feed for the POS header bell. Each source is a small Firestore listener
 * (staff-readable collections only) — add a new source here to surface another kind of alert.
 * A source that fails (missing permission on a project, offline) is skipped, never breaks the bell.
 */
const SOURCES: Source[] = [
  {
    kind: 'catering',
    build: () => query(collection(getFirestoreDB(), 'catering_inquiries'), where('status', '==', 'new'), limit(25)),
    map: (id, d) => ({
      id: `catering-${id}`,
      kind: 'catering',
      title: `Catering request · ${OCCASIONS[d.occasion] ?? 'Event'}`,
      detail: [d.contactName, d.guestCount ? `${d.guestCount} people` : d.guestBand, d.eventDate].filter(Boolean).join(' · '),
      at: toMillis(d.createdAt),
      page: 'catering',
    }),
  },
  {
    kind: 'chat',
    build: () => query(collection(getFirestoreDB(), 'app_chat_threads'), where('unreadByStaff', '>', 0), limit(25)),
    map: (id, d) => ({
      id: `chat-${id}`,
      kind: 'chat',
      title: `New message · ${d.customerName || 'Customer'}`,
      detail: String(d.lastMessageText || '').slice(0, 80),
      at: toMillis(d.lastMessageAt),
      page: 'customer-app-chat',
    }),
  },
  {
    kind: 'feedback',
    build: () => query(collection(getFirestoreDB(), 'app_feedback'), where('status', '==', 'open'), limit(25)),
    map: (id, d) => ({
      id: `feedback-${id}`,
      kind: 'feedback',
      title: `App feedback · ${d.category || 'feedback'}`,
      detail: String(d.message || '').slice(0, 80),
      at: toMillis(d.createdAt),
      page: 'customer-app-feedback',
    }),
  },
  {
    kind: 'meal_plan_exception',
    build: () => query(collection(getFirestoreDB(), 'meal_plan_exception_requests'), where('status', '==', 'pending'), limit(25)),
    map: (id, d) => ({
      id: `mpex-${id}`,
      kind: 'meal_plan_exception',
      title: 'Meal plan exception request',
      detail: [d.customerName || d.customerEmail, d.reason].filter(Boolean).join(' · ').slice(0, 80),
      at: toMillis(d.createdAt),
      page: 'settings',
    }),
  },
];

/** How many read alerts the bell shows at first, and how many each "load more" adds. */
const READ_PAGE_FIRST = 4;
const READ_PAGE_MORE = 10;

/** An alert staff already opened — kept (as a snapshot) so it stays viewable in the bell. */
export interface ReadStaffNotification extends StaffNotification {
  seenAt: number;
}

export function useStaffNotifications(enabled = true): {
  /** Unread alerts (what the badge counts). */
  items: StaffNotification[];
  count: number;
  /** Recently opened alerts, newest first, paged via [loadMoreRead]. */
  readItems: ReadStaffNotification[];
  hasMoreRead: boolean;
  loadMoreRead: () => void;
  /** Back to the first page of read alerts (called when the bell closes). */
  resetReadPage: () => void;
  /** Dismisses an alert everywhere (shared across terminals) once staff open it. */
  markSeen: (item: StaffNotification) => void;
} {
  const [bySource, setBySource] = useState<Record<string, StaffNotification[]>>({});
  const [sharedSeen, setSharedSeen] = useState<Set<string>>(new Set());
  const [readHistory, setReadHistory] = useState<ReadStaffNotification[]>([]);
  const [readLimit, setReadLimit] = useState(READ_PAGE_FIRST);
  const [localSeen, setLocalSeen] = useState<Set<string>>(() => readLocalSeen());

  useEffect(() => {
    if (!enabled) return undefined;
    const unsubs = SOURCES.map((source) => {
      try {
        return onSnapshot(
          source.build(),
          (snap) => {
            setBySource((prev) => ({ ...prev, [source.kind]: snap.docs.map((d) => source.map(d.id, d.data())) }));
          },
          (err) => {
            console.warn(`Staff notifications: ${source.kind} unavailable`, err.message);
            setBySource((prev) => ({ ...prev, [source.kind]: [] }));
          },
        );
      } catch (err) {
        console.warn(`Staff notifications: ${source.kind} failed to start`, err);
        return () => {};
      }
    });

    // Alerts dismissed on any terminal.
    const seenUnsub = onSnapshot(
      query(collection(getFirestoreDB(), SEEN_COLLECTION), orderBy('seenAt', 'desc'), limit(500)),
      (snap) => {
        setSharedSeen(new Set(snap.docs.map((d) => d.id)));
        setReadHistory(
          snap.docs
            .map((d) => d.data())
            // Older entries (before snapshots were stored) have no title — nothing to show.
            .filter((d) => typeof d.title === 'string' && typeof d.page === 'string')
            .map((d) => ({
              id: String(d.alertId ?? ''),
              kind: d.kind,
              title: d.title,
              detail: String(d.detail ?? ''),
              at: Number(d.at) || 0,
              page: d.page as Page,
              seenAt: Number(d.seenAt) || 0,
            })),
        );
      },
      (err) => console.warn('Staff notifications: shared seen state unavailable', err.message),
    );

    return () => {
      unsubs.forEach((unsub) => unsub());
      seenUnsub();
    };
  }, [enabled]);

  const markSeen = useCallback((item: StaffNotification) => {
    const key = seenKey(item);
    setLocalSeen((prev) => {
      const next = new Set(prev).add(key);
      writeLocalSeen(next);
      return next;
    });
    // Store a snapshot so the alert stays viewable under "Earlier" even after its source is
    // handled (e.g. a chat thread with no unread messages no longer appears in the live feed).
    setDoc(doc(getFirestoreDB(), SEEN_COLLECTION, key), {
      seenAt: Date.now(),
      alertId: item.id,
      kind: item.kind,
      title: item.title,
      detail: item.detail,
      at: item.at,
      page: item.page,
    }).catch((err) => {
      // Still dismissed on this terminal via localStorage.
      console.warn('Staff notifications: could not share seen state', err?.message);
    });
  }, []);

  const loadMoreRead = useCallback(() => setReadLimit((n) => n + READ_PAGE_MORE), []);
  const resetReadPage = useCallback(() => setReadLimit(READ_PAGE_FIRST), []);

  return useMemo(() => {
    const items = Object.values(bySource)
      .flat()
      .filter((item) => {
        const key = seenKey(item);
        return !sharedSeen.has(key) && !localSeen.has(key);
      })
      .sort((a, b) => b.at - a.at);
    return {
      items,
      count: items.length,
      readItems: readHistory.slice(0, readLimit),
      hasMoreRead: readHistory.length > readLimit,
      loadMoreRead,
      resetReadPage,
      markSeen,
    };
  }, [bySource, sharedSeen, localSeen, readHistory, readLimit, loadMoreRead, resetReadPage, markSeen]);
}
